import React, { Component, type ReactNode } from 'react';
import getScrollbarSize from '../../../utils/get-scrollbar-size';
import {
	Provider,
	type RequestBoundingClient,
	type RequestScrollBounding,
	type ScrollListenerFunction,
} from '../context';

const MISSING_REF_ERROR_MESSAGE =
	'Viewport element was null. Only make this request when it is certain to be available (e.g. not in componentDidMount)';

type Props = {
	children: ReactNode;
};

type State = {
	requestViewportBoundingClient: RequestBoundingClient;
	requestViewportScrollBounding: RequestScrollBounding;
	listenToViewportScroll: ScrollListenerFunction;
	stopListeningToViewportScroll: ScrollListenerFunction;
	setRef: (elem: HTMLElement | null) => void;
};

// eslint-disable-next-line jira/react/no-class-components
class ViewportProvider extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		// It is being used, but only as a whole state object and the linter does not recognize that
		/* eslint-disable react/no-unused-state */
		this.state = {
			requestViewportBoundingClient: this.requestBoundingClient,
			requestViewportScrollBounding: this.requestScrollBounding,
			listenToViewportScroll: this.listenToScroll,
			stopListeningToViewportScroll: this.stopListeningToScroll,
			setRef: this.onSetRef,
		};
		/* eslint-enable react/no-unused-state */
	}

	// eslint-disable-next-line jira/react-arrow-function-property-naming
	requestBoundingClient: RequestBoundingClient = () => {
		const viewportOffset = getScrollbarSize();
		if (!this.ref) {
			throw new Error(MISSING_REF_ERROR_MESSAGE);
		}
		if (viewportOffset) {
			const boundingClient = this.ref.getBoundingClientRect();

			const transformedBoundingClient = {
				width: boundingClient.width - viewportOffset,
				height: boundingClient.height - viewportOffset,
				right: boundingClient.right - viewportOffset,
				bottom: boundingClient.bottom - viewportOffset,
				left: boundingClient.left,
				top: boundingClient.top,
			};

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return transformedBoundingClient as ClientRect;
		}

		return this.ref.getBoundingClientRect();
	};

	// eslint-disable-next-line jira/react-arrow-function-property-naming
	requestScrollBounding: RequestScrollBounding = () => {
		if (!this.ref) {
			throw new Error(MISSING_REF_ERROR_MESSAGE);
		}

		const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientWidth, clientHeight } =
			this.ref;

		// TODO getting clientWidth causes reflow - can we memoize clientWidth somehow?
		// The table will only change size on window resize or popping out the sidebar.
		return {
			scrollTop,
			scrollLeft,
			scrollRight: scrollWidth > clientWidth ? scrollWidth - clientWidth - scrollLeft : 0,
			scrollHeight,
			scrollWidth,
			isOverflowY: scrollHeight > clientHeight,
		};
	};

	// eslint-disable-next-line jira/react-arrow-function-property-naming
	listenToScroll: ScrollListenerFunction = (callback) => {
		if (!this.ref) {
			throw new Error(MISSING_REF_ERROR_MESSAGE);
		}

		this.ref.addEventListener('scroll', callback);
	};

	// eslint-disable-next-line jira/react-arrow-function-property-naming
	stopListeningToScroll: ScrollListenerFunction = (callback) => {
		if (this.ref) {
			this.ref.removeEventListener('scroll', callback);
		}
	};

	onSetRef = (elem: HTMLElement | null) => {
		this.ref = elem;
	};

	// @ts-expect-error - TS2564 - Property 'ref' has no initializer and is not definitely assigned in the constructor.
	ref: HTMLElement | null;

	render() {
		const { children } = this.props;
		return <Provider value={this.state}>{children}</Provider>;
	}
}

export default ViewportProvider;
